import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

export default function MyBookings() {
    const navigate = useNavigate();
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [user, setUser] = useState(null);

    // Check for auth and load user
    useEffect(() => {
        const token = localStorage.getItem('emba_access');
        const userData = localStorage.getItem('emba_user');

        if (!token) {
            navigate('/login');
            return;
        }

        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                // Handle potential nested user object structure
                const actualUser = parsedUser.user || parsedUser;
                setUser(actualUser);
            } catch (e) {
                console.error("Failed to parse user data", e);
                navigate('/login');
            }
        }
    }, [navigate]);

    // Fetch bookings once user is loaded
    useEffect(() => {
        if (!user || !user.id) return;

        const fetchBookings = async () => {
            setLoading(true);
            setError('');
            try {
                const token = localStorage.getItem('emba_access');
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/appointments/get-appointment-by-userid/?appointment_user_id=${user.id}`, {
                    method: 'GET',
                    headers: {
                        'accept': '*/*',
                        'Authorization': `Bearer ${token}`
                    }
                });

                const data = await res.json().catch(() => []);

                if (!res.ok) {
                    // If 404, it might just mean no bookings, but let's handle error generally
                    // If the API returns 404 for "no bookings found", we should treat it as empty array.
                    // Assuming standard REST, 200 OK with empty array is better, but let's be safe.
                    if (res.status === 404) {
                        setBookings([]);
                        return;
                    }
                    throw new Error(data?.detail || data?.message || 'Failed to fetch bookings');
                }

                // Ensure data is an array
                setBookings(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(err);
                setError('Could not load your bookings. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        fetchBookings();
    }, [user]);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return new Intl.DateTimeFormat('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(date);
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        // timeStr is likely "HH:MM:SS"
        const [h, m] = timeStr.split(':');
        const date = new Date();
        date.setHours(parseInt(h, 10));
        date.setMinutes(parseInt(m, 10));
        return new Intl.DateTimeFormat('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        }).format(date);
    };

    return (
        <div className="min-h-screen bg-neutral-50 text-neutral-900 font-sans">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <Link to="/" className="text-xs tracking-[0.2em] font-bold uppercase hover:opacity-70 transition-opacity">
                        EMBA by Emefa
                    </Link>

                    <div className="flex items-center gap-6">
                        {user && (
                            <span className="text-[10px] tracking-widest uppercase font-medium text-gray-500 hidden md:block">
                                Welcome, {user.first_name}
                            </span>
                        )}
                        <nav className="hidden md:flex gap-8 text-[10px] tracking-widest uppercase font-medium text-gray-500">
                            <Link to="/" className="hover:text-black transition-colors">Home</Link>
                            <Link to="/profile" className="hover:text-black transition-colors">Account</Link>
                        </nav>
                        <Link to="/bookings" className="text-[10px] uppercase tracking-widest bg-black text-white px-4 py-2 hover:bg-gray-800 transition-colors">
                            Book Now
                        </Link>
                    </div>
                </div>
            </header>

            <main className="pt-32 pb-20 px-4 md:px-6 max-w-5xl mx-auto">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2">My Bookings</h1>
                        <p className="text-sm text-neutral-600">View and manage your scheduled appointments.</p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-20">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900"></div>
                    </div>
                ) : error ? (
                    <div className="rounded-lg bg-red-50 border border-red-100 p-4 text-sm text-red-600 text-center">
                        {error}
                    </div>
                ) : bookings.length === 0 ? (
                    <div className="text-center py-24 border border-dashed border-neutral-200 rounded-2xl bg-white">
                        <p className="text-neutral-400 text-sm mb-6">You have no upcoming bookings.</p>
                        <Link to="/bookings" className="inline-block border border-neutral-200 bg-white px-6 py-3 text-xs font-bold uppercase tracking-widest hover:border-neutral-900 hover:bg-neutral-50 transition-colors rounded-lg">
                            Schedule Appointment
                        </Link>
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {bookings.map((booking) => (
                            <motion.div
                                key={booking.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm transition-all hover:shadow-md md:p-8"
                            >
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${booking.paid ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
                                                {booking.paid ? 'Confirmed' : 'Pending Payment'}
                                            </span>
                                            <span className="text-[10px] text-neutral-400 uppercase tracking-wider">
                                                ID: {booking.id.slice(0, 8)}
                                            </span>
                                        </div>
                                        <h3 className="text-xl font-medium text-neutral-900">
                                            {formatDate(booking.appointment_date)}
                                        </h3>
                                        <p className="text-neutral-500 mt-1 flex items-center gap-2">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                                            {formatTime(booking.appointment_time)}
                                        </p>
                                    </div>

                                    <div className="flex items-center gap-4 border-t md:border-t-0 md:border-l border-neutral-100 pt-4 md:pt-0 md:pl-8">
                                        <div className="text-right hidden md:block">
                                            <p className="text-[10px] uppercase tracking-widest text-neutral-400">Amount</p>
                                            <p className="font-medium">GHS {booking.amount}</p>
                                        </div>
                                        {/* Future: Add Cancel/Reschedule buttons here */}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
